import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClients } from '@/api/client';
import type { components } from '@/api/types/api-gateway';

// S15 — Roles screen. Reads the full capability matrix (R1) +
// per-tenant override (R4).

export type CapabilityMatrix = components['schemas']['CapabilityMatrix'];
export type PolicyResponse = components['schemas']['PolicyResponse'];

function unwrap<T>(data: T | undefined, error: unknown, what: string): T {
  if (error || !data) {
    const msg = (error as { error?: string } | undefined)?.error ?? `${what} failed`;
    throw new Error(msg);
  }
  return data;
}

export function useCapabilityMatrix(clients: ApiClients) {
  return useQuery({
    queryKey: ['capabilities', 'matrix'],
    queryFn: async (): Promise<CapabilityMatrix> => {
      const { data, error } = await clients.apiGateway.GET('/v1/capabilities');
      return unwrap(data, error, 'capabilities');
    },
    staleTime: 5 * 60_000,
  });
}

export function usePolicy(clients: ApiClients) {
  return useQuery({
    queryKey: ['policies', 'current'],
    queryFn: async (): Promise<PolicyResponse> => {
      const { data, error } = await clients.apiGateway.GET('/v1/policies');
      return unwrap(data, error, 'policy');
    },
    staleTime: 30_000,
  });
}

export function usePutPolicy(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { override: Record<string, string[]> }) => {
      const { data, error } = await clients.apiGateway.PUT('/v1/policies', {
        body: { override: vars.override },
      });
      if (error) throw new Error('save policy failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

// Audit-list helper used by the read-view "View policy change history"
// link in S15. Pre-built so the /admin/audit landing can use the same
// hook for its policy-scoped tab.
export type AuditEntry = components['schemas']['AuditEntry'];
export type AuditLog = components['schemas']['AuditLog'];

export function usePolicyAuditEntries(clients: ApiClients, limit = 20) {
  return useQuery({
    queryKey: ['audit-log', 'policy', limit],
    queryFn: async (): Promise<AuditLog> => {
      const { data, error } = await clients.apiGateway.GET('/v1/audit-log', {
        params: { query: { target_type: 'org', limit } },
      });
      return unwrap(data, error, 'policy audit log');
    },
    staleTime: 30_000,
  });
}

export function useResetPolicy(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await clients.apiGateway.POST('/v1/policies/reset');
      if (error) throw new Error('reset policy failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

export function usePutUserPolicy(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string; capabilities: string[] }) => {
      const { data, error } = await clients.apiGateway.PUT(
        '/v1/policies/users/{userId}',
        {
          params: { path: { userId: vars.userId } },
          body: { capabilities: vars.capabilities },
        },
      );
      if (error) throw new Error('save user override failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

export function useDeleteUserPolicy(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await clients.apiGateway.DELETE(
        '/v1/policies/users/{userId}',
        { params: { path: { userId } } },
      );
      if (error) throw new Error('reset user override failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}
